import { useEffect, useState } from "react";
import { NavLink, Route, Routes, Navigate, Link } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, Shirt, ShoppingCart, Users, Palette, Building2, Film, Sparkles, Percent, Layers, Globe, Mail, UserCog, Loader2, Menu } from "lucide-react";
import { toast } from "sonner";
import { useAuthedGet, useAuthedHttp, errMsg, inr, useGet } from "@/lib/api";
import { useSeo } from "@/lib/store";
import { Logo } from "@/components/Brand";
import { STATUS_LABEL } from "@/pages/Orders";
import { ResourceManager } from "./ResourceManager";
import { Enquiries } from "./Enquiries";
import { Field } from "./Fields";
import * as S from "./schemas";

const NAV = [["", "Overview", LayoutGrid], ["products", "Products", Shirt], ["orders", "Orders", ShoppingCart], ["customers", "Customers", Users], ["custom", "Custom Enquiries", Palette], ["wholesale", "Wholesale", Building2], ["reels", "Reels", Film], ["anime", "Anime Designs", Sparkles], ["offers", "Offers", Percent], ["categories", "Categories", Layers], ["website", "Website", Globe], ["messages", "Messages", Mail], ["users", "Users", UserCog]];

function Overview() {
  const { data: s } = useAuthedGet("/admin/stats");
  if (!s) return <Loader2 className="animate-spin" />;
  const cards = [["Revenue (paid)", inr(s.revenue), "orders"], ["Paid orders", s.paid_orders, "orders"], ["All orders", s.orders, "orders"], ["New custom enquiries", s.custom_new, "custom"], ["New wholesale enquiries", s.wholesale_new, "wholesale"], ["Products", s.products, "products"], ["Low stock (<10)", s.low_stock, "products"], ["Reels", s.reels, "reels"], ["Subscribers", s.subscribers, "messages"], ["Contact messages", s.messages, "messages"]];
  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-5xl mb-8">DASHBOARD</h1>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map(([l, v, to]) => <Link key={l} to={`/admin/${to}`} className="bg-[#171717] p-5 hover:outline hover:outline-1 hover:outline-[#C6FF00]" data-testid={`stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}><p className="font-mono text-[10px] uppercase text-grey tracking-widest">{l}</p><p className="font-display text-4xl mt-2">{v}</p></Link>)}
      </div>
    </div>
  );
}

function Orders() {
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data = [] } = useAuthedGet(`/admin/orders?status=${status}`);
  const [open, setOpen] = useState(null);
  const update = async (o, st, tracking = o.tracking || "") => {
    try { await call("patch", `/admin/orders/${o.id}`, { status: st, tracking }); toast.success("Order updated"); qc.invalidateQueries({ queryKey: ["authed"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-4xl mb-6">ORDERS</h1>
      <div className="flex flex-wrap gap-2 mb-6">{["", ...Object.keys(STATUS_LABEL)].map((s) => <button key={s || "all"} onClick={() => setStatus(s)} className={`chip ${status === s ? "on" : ""}`}>{s ? STATUS_LABEL[s] : "All"}</button>)}</div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-[#171717] text-left font-mono text-[11px] uppercase text-grey"><tr>{["Order", "Customer", "Items", "Total", "Payment", "Fulfilment", "Date"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody>
            {data.map((o) => (
              <tr key={o.id} className="border-t border-white/10 align-top" data-testid={`admin-order-row-${o.order_no}`}>
                <td className="p-3 font-mono text-neon cursor-pointer" onClick={() => setOpen(open === o.id ? null : o.id)}>{o.order_no}</td>
                <td className="p-3">{o.address.name}<p className="text-xs text-grey">{o.address.phone} · {o.address.city}</p></td>
                <td className="p-3 text-xs">{o.lines.map((l, i) => <p key={i}>{l.title} ({l.size}) ×{l.qty}</p>)}{open === o.id && <p className="text-grey mt-2">{o.address.line1}, {o.address.line2} {o.address.city}, {o.address.state} {o.address.pincode} · {o.address.email}</p>}</td>
                <td className="p-3 font-mono">{inr(o.total)}{o.coupon && <p className="text-[10px] text-neon">{o.coupon}</p>}</td>
                <td className="p-3 font-mono text-[11px]">{o.payment.status}<p className="text-grey">{o.payment.razorpay_payment_id || o.payment.razorpay_order_id}</p></td>
                <td className="p-3 space-y-1">
                  <select className="field py-1.5 text-xs" value={o.status} onChange={(e) => update(o, e.target.value)} data-testid={`admin-order-status-${o.order_no}`}>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                  <input className="field py-1.5 text-xs" defaultValue={o.tracking} placeholder="Tracking no." onBlur={(e) => e.target.value !== (o.tracking || "") && update(o, o.status, e.target.value)} />
                </td>
                <td className="p-3 text-xs text-grey">{new Date(o.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {!data.length && <tr><td colSpan={7} className="p-8 text-center text-grey">No orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Customers() {
  const { data = [] } = useAuthedGet("/admin/customers");
  return (
    <div data-testid="admin-customers">
      <h1 className="font-display text-4xl mb-6">CUSTOMERS</h1>
      <table className="w-full text-sm border border-white/10"><thead className="bg-[#171717] text-left font-mono text-[11px] uppercase text-grey"><tr>{["Name", "Email", "Phone", "City", "Orders", "Spent"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>{data.map((c) => <tr key={c.email} className="border-t border-white/10"><td className="p-3">{c.name}</td><td className="p-3">{c.email}</td><td className="p-3">{c.phone}</td><td className="p-3">{c.city}</td><td className="p-3 font-mono">{c.orders}</td><td className="p-3 font-mono">{inr(c.spent)}</td></tr>)}
          {!data.length && <tr><td colSpan={6} className="p-8 text-center text-grey">No customers yet.</td></tr>}</tbody></table>
    </div>
  );
}

const SETTINGS = [
  { name: "announcement", label: "Announcement bar text", wide: true }, { name: "hero_headline", label: "Hero headline" }, { name: "hero_sub", label: "Hero subheading" },
  { name: "hero_image", label: "Hero banner image", type: "image" }, { name: "hero_image_2", label: "Hero side card image", type: "image" },
  { name: "promo_title", label: "Wholesale promo title" }, { name: "promo_text", label: "Wholesale promo text" },
  { name: "phone", label: "Phone" }, { name: "whatsapp", label: "WhatsApp (with country code, e.g. 919214356156)" }, { name: "email", label: "Email" },
  { name: "address", label: "Address", wide: true }, { name: "instagram", label: "Instagram URL" }, { name: "facebook", label: "Facebook URL" }, { name: "youtube", label: "YouTube URL" },
  { name: "featured_collections", label: "Featured collection slugs", type: "list" }, { name: "gallery", label: "Instagram gallery images", type: "images" },
];

function Website() {
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const { data } = useGet("/settings");
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return <Loader2 className="animate-spin" />;
  const save = async (e) => {
    e.preventDefault();
    try { await call("put", "/admin/settings", s); toast.success("Website updated"); qc.invalidateQueries({ queryKey: ["/settings"] }); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={save} data-testid="admin-website">
      <h1 className="font-display text-4xl mb-6">WEBSITE MANAGEMENT</h1>
      <div className="grid sm:grid-cols-2 gap-5">
        {SETTINGS.map((f) => <div key={f.name} className={f.wide || ["image", "images"].includes(f.type) ? "sm:col-span-2" : ""}><span className="label">{f.label}</span><Field f={f} value={s[f.name]} onChange={(v) => setS({ ...s, [f.name]: v })} /></div>)}
      </div>
      <button className="btn-neon mt-8" data-testid="admin-website-save">Save website settings</button>
    </form>
  );
}

function Messages() {
  const { data } = useAuthedGet("/admin/messages");
  return (
    <div data-testid="admin-messages">
      <h1 className="font-display text-4xl mb-6">MESSAGES & SUBSCRIBERS</h1>
      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <div className="space-y-3">{data?.messages.map((m) => <div key={m.id} className="bg-[#171717] p-4"><p className="font-semibold">{m.name} <span className="text-grey text-xs">{m.email} {m.phone}</span></p><p className="text-sm mt-2">{m.message}</p><p className="text-[10px] text-grey mt-2 font-mono">{new Date(m.created_at).toLocaleString()}</p></div>)}{!data?.messages.length && <p className="text-grey">No messages.</p>}</div>
        <div className="bg-[#171717] p-4"><p className="label">Newsletter ({data?.subscribers.length || 0})</p>{data?.subscribers.map((s) => <p key={s.email} className="text-sm py-1 border-b border-white/5">{s.email}</p>)}</div>
      </div>
    </div>
  );
}

function UsersAdmin() {
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const { data } = useAuthedGet("/users");
  const change = async (u, role) => { try { await call("patch", `/users/${u.user_id}/role`, { role }); toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["authed", "/users"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="admin-users">
      <h1 className="font-display text-4xl mb-6">USERS & ROLES</h1>
      <table className="w-full text-sm border border-white/10"><thead className="bg-[#171717] text-left font-mono text-[11px] uppercase text-grey"><tr><th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Role</th></tr></thead>
        <tbody>{data?.users.map((u) => <tr key={u.user_id} className="border-t border-white/10"><td className="p-3">{u.email || u.user_id}</td><td className="p-3">{u.name}</td><td className="p-3"><select className="field py-1.5 w-36" value={u.role} onChange={(e) => change(u, e.target.value)} data-testid={`user-role-${u.user_id}`}>{data.roles.map((r) => <option key={r}>{r}</option>)}</select></td></tr>)}</tbody></table>
    </div>
  );
}

function Shell() {
  const { data: me, isLoading, error } = useAuthedGet("/me");
  const { data: cats = [] } = useGet("/categories");
  const [open, setOpen] = useState(false);
  if (isLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin" /></div>;
  if (error || me?.role !== "admin") return (
    <div className="min-h-screen grid place-items-center text-center p-6" data-testid="admin-access-denied"><div><p className="font-display text-5xl">ADMINS ONLY.</p><p className="text-grey mt-3">Your account doesn't have admin access.</p><Link to="/" className="btn-neon mt-6">Back to store</Link></div></div>
  );
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[240px_1fr]" data-testid="admin-dashboard">
      <aside className={`${open ? "block" : "hidden"} lg:block bg-[#050505] border-r border-white/10 lg:sticky lg:top-0 lg:h-screen overflow-y-auto`}>
        <div className="p-5 border-b border-white/10"><Logo size="text-xl" /><p className="font-mono text-[10px] text-neon mt-1 tracking-widest">ADMIN CONSOLE</p></div>
        <nav className="p-3 space-y-0.5">
          {NAV.map(([to, l, I]) => <NavLink key={to} to={`/admin/${to}`} end onClick={() => setOpen(false)} data-testid={`admin-nav-${to || "overview"}`} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-neon text-black font-semibold" : "text-white/70 hover:bg-white/5"}`}><I className="w-4 h-4" />{l}</NavLink>)}
        </nav>
      </aside>
      <div className="min-w-0">
        <header className="h-14 border-b border-white/10 flex items-center justify-between px-5">
          <button className="lg:hidden" onClick={() => setOpen((o) => !o)} data-testid="admin-menu-toggle"><Menu /></button>
          <Link to="/" className="text-xs uppercase tracking-widest text-grey hover:text-neon">← View store</Link>
          <Show when="signed-in"><UserButton /></Show>
        </header>
        <main className="p-5 sm:p-8">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="products" element={<ResourceManager res="products" title="PRODUCTS" schema={S.productSchema(cats)} columns={S.productCols} />} />
            <Route path="orders" element={<Orders />} />
            <Route path="customers" element={<Customers />} />
            <Route path="custom" element={<Enquiries kind="custom" />} />
            <Route path="wholesale" element={<Enquiries kind="wholesale" />} />
            <Route path="reels" element={<ResourceManager res="reels" title="REELS" schema={S.reelSchema} columns={S.reelCols} />} />
            <Route path="anime" element={<ResourceManager res="anime" title="ANIME DESIGNS" schema={S.animeSchema} columns={S.animeCols} />} />
            <Route path="offers" element={<ResourceManager res="offers" title="OFFERS" schema={S.offerSchema} columns={S.offerCols} />} />
            <Route path="categories" element={<ResourceManager res="categories" title="CATEGORIES" schema={S.categorySchema} columns={S.categoryCols} />} />
            <Route path="website" element={<Website />} />
            <Route path="messages" element={<Messages />} />
            <Route path="users" element={<UsersAdmin />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default function Admin() {
  useSeo("Admin");
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return null;
  return isSignedIn ? <Shell /> : <Navigate to="/sign-in" replace />;
}
